<template>
    <TransitionRoot as="template" :show="show && !showSwitch">
        <Dialog as="div" class="relative z-50" @close="$emit('dismiss')">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-900/70 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative w-full max-w-xl transform overflow-hidden rounded-2xl bg-white px-10 pt-10 pb-8 text-center shadow-2xl transition-all sm:my-8">
                            <button
                                type="button"
                                @click="$emit('dismiss')"
                                class="absolute top-5 right-5 text-gray-400 hover:text-gray-700 focus:outline-none"
                            >
                                <font-awesome-icon :icon="faXmark" class="h-5 w-5" />
                                <span class="sr-only">Close</span>
                            </button>

                            <DialogTitle as="h3" class="text-2xl font-bold tracking-tight text-[#c19b1c]">
                                Upgrade Your Account to Pro
                            </DialogTitle>
                            <p class="mt-1 text-sm text-gray-500">Access everything, for every property</p>

                            <ul v-if="showFeatures" class="mx-auto mt-5 grid max-w-md grid-cols-1 gap-x-6 gap-y-2 text-left sm:grid-cols-2">
                                <li v-for="feature in features" :key="feature" class="flex items-center text-sm text-gray-700">
                                    <font-awesome-icon :icon="faSquareCheck" class="mr-2 h-4 w-4 shrink-0 text-green-600" />
                                    {{ feature }}
                                </li>
                            </ul>

                            <p v-if="notice" class="mx-auto mt-4 max-w-md rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert" data-test="upsell-notice">
                                {{ notice }}
                            </p>

                            <p v-if="lowestTier" class="mt-5 text-xs text-gray-500">
                                <ToolTip title="Starting at" class="!inline">
                                    <template #trigger>
                                        <span class="cursor-help underline">Starting at</span>
                                    </template>
                                    <StartingAtPopupTable :rows="startingAtRows" show-title />
                                </ToolTip>
                                {{ formatMonthly(lowestTier.monthly_equivalent) }}/mo for your first {{ lowestTier.max }} properties.
                            </p>

                            <p v-if="isLegacy" class="mt-4 text-sm font-semibold text-gray-900">
                                You're on an earlier RentRisk plan. Switch to the new plan to get Pro on every property.
                            </p>
                            <template v-else>
                                <!-- 86bc9e403: dashboard entry points open on an "Upgrade to Pro" CTA and
                                     keep the checkout below hidden until it is clicked. On /pricing
                                     (showFeatures false) the checkout is shown straight away. -->
                                <div v-if="showUpgradeCta" class="mx-auto mt-6 max-w-md">
                                    <button
                                        type="button"
                                        class="w-full rounded-lg bg-[#2f3a5e] px-6 py-3.5 text-base font-bold text-white shadow-sm hover:bg-[#263150] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2f3a5e]"
                                        data-test="expand-checkout"
                                        @click="expanded = true"
                                    >
                                        Upgrade to Pro
                                    </button>
                                </div>

                                <template v-if="showCheckout">
                                <!-- Order summary: the property count drives the live yearly price. -->
                                <div class="mx-auto mt-6 max-w-md rounded-xl border border-gray-200 bg-gray-50 p-5 text-left">
                                    <div v-if="showPropertyCountInput">
                                        <label for="pro-property-count" class="block text-sm font-medium text-gray-700">
                                            How many properties do you manage?
                                        </label>
                                        <input
                                            id="pro-property-count"
                                            v-model="enteredPropertyCount"
                                            type="number"
                                            :min="countFloor"
                                            step="1"
                                            inputmode="numeric"
                                            required
                                            class="mt-1.5 block w-full rounded-lg border-gray-300 shadow-sm focus:border-rent-risk-blue-400 focus:ring-rent-risk-blue-400 sm:text-sm"
                                            data-test="pro-property-count"
                                        >
                                        <p v-if="belowFloor" class="mt-1 text-sm text-red-600" data-test="pro-count-floor-hint">
                                            Enter at least {{ countFloor }}.
                                        </p>
                                    </div>

                                    <p v-if="effectiveTier" class="mt-4 border-t border-gray-200 pt-4 text-base font-semibold text-gray-900" data-test="pro-price-summary">
                                        Your price: <span class="text-[#2f3a5e]">{{ formatYearly(effectiveTier.price) }}</span> for your {{ effectiveCount }} {{ effectiveCount === 1 ? 'property' : 'properties' }}.
                                    </p>
                                </div>

                                <!-- 86bc9e403: combined checkout — the card lives in the same pop-up, so the
                                     landlord enters a count, a card (if needed) and subscribes in one place. -->
                                <div v-if="combinedMode" class="mx-auto mt-5 max-w-md text-left">
                                    <h4 class="text-sm font-semibold text-gray-900">Payment details</h4>
                                    <div v-if="hasOnFileCard && !changeCard" class="mt-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3" data-test="on-file-card">
                                        <span class="text-sm text-gray-800">
                                            <span class="font-semibold uppercase">{{ effectivePaymentMethod.card_brand || 'Card' }}</span>
                                            ending {{ effectivePaymentMethod.last_4 }}
                                        </span>
                                        <button type="button" class="text-sm font-medium text-rent-risk-blue-400 underline hover:text-black" data-test="change-card" @click="startChangeCard">
                                            Change
                                        </button>
                                    </div>
                                    <div v-else-if="effectiveStripeData" class="mt-2">
                                        <CardFields
                                            ref="cardFields"
                                            :key="cardFieldsKey"
                                            :stripe-data="effectiveStripeData"
                                            :visible="show"
                                            :apple-pay-recurring="applePayRecurring"
                                            data-test="card-fields"
                                            @complete="cardComplete = $event"
                                        />
                                    </div>
                                    <p v-if="subscribeError" class="mt-2 text-sm text-red-600" data-test="subscribe-error">{{ subscribeError }}</p>
                                    <p class="mt-3 text-xs text-gray-500">
                                        By subscribing you agree to our
                                        <a :href="route('privacy-policy')" target="_blank" class="underline">Privacy Policy</a>
                                        and
                                        <a :href="route('terms-and-conditions')" target="_blank" class="underline">Terms and Conditions</a>.
                                    </p>
                                </div>
                                </template>
                            </template>

                            <div v-if="isLegacy || showCheckout" class="mx-auto mt-6 max-w-md">
                                <button
                                    type="button"
                                    :disabled="!canSubmit"
                                    class="w-full rounded-lg bg-[#2f3a5e] px-6 py-3.5 text-base font-bold text-white shadow-sm hover:bg-[#263150] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2f3a5e] disabled:cursor-not-allowed disabled:opacity-60"
                                    @click="primaryAction"
                                >
                                    {{ primaryLabel }}
                                </button>
                            </div>

                            <button
                                v-if="declineLabel"
                                type="button"
                                class="mx-auto mt-4 block text-sm text-blue-600 underline hover:text-blue-800"
                                @click="$emit('decline')"
                            >
                                {{ declineLabel }}
                            </button>

                            <button
                                type="button"
                                class="mx-auto mt-3 block text-sm text-gray-600 underline hover:text-gray-900"
                                @click="$emit('dismiss')"
                            >
                                Cancel
                            </button>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <SwitchToNewPlanModal
        :show="showSwitch"
        :stripe-data="stripeData"
        @close="closeSwitch"
        @switched="closeSwitch"
    />
</template>

<script setup>
// The RiskGuard Pro upgrade pop-up (86bcd9hg3, ticket design + decision 24): Pro is one
// yearly plan for the whole account, priced by property count. Shows the tier table
// behind "Starting at" and the landlord's own price before anything is charged.
//
// 86bc9e403: this is now a single, in-place combined checkout. When a caller passes the
// `subscription` ref (its SubscriptionProtection instance), the pop-up collects the property
// count AND the card in one place and subscribes without leaving the page or redirecting to
// /pricing. Callers that pass no `subscription` keep the earlier emit-based behavior — they
// handle `upgrade` themselves (the contextual tab upsells with their $29 / $49 decline paths).
//
// A landlord on an earlier plan can't buy Pro on top of it (decision 12), so for them
// the primary button opens "Switch to new plan" right here — every caller gets that
// without wiring it up.
//
// `decline` fires only from the optional decline link (the $29 report or $49 lease
// bought on its own instead, decision 19); `dismiss` from Cancel, X, backdrop or Esc.
import { computed, ref, watch } from 'vue'
import axios from 'axios'
import { usePage } from '@inertiajs/vue3'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSquareCheck, faXmark } from '@fortawesome/pro-solid-svg-icons'
import ToolTip from '@/Shared/ToolTip.vue'
import StartingAtPopupTable from '@/Pages/V2/Properties/Components/StartingAtPopupTable.vue'
import SwitchToNewPlanModal from '@/Components/SwitchToNewPlanModal.vue'
import CardFields from '@/Components/CardFields.vue'
import { buildStartingAtRows } from '@/Pages/V2/Properties/startingAtPopup'
import { formatMonthly, formatYearly } from '@/Shared/proPlanState'

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    processing: {
        type: Boolean,
        default: false,
    },
    // The landlord's pricing summary. Defaults to the shared auth.user.pro_plan; pages
    // without a signed-in user (old pending_user_id sessions) pass their own.
    proPlan: {
        type: Object,
        default: null,
    },
    // Shown above the price, e.g. why the pop-up came back with a new price (86bcd9hg3).
    notice: {
        type: String,
        default: null,
    },
    // Text of the "buy it on its own instead" link; no link when null.
    declineLabel: {
        type: String,
        default: null,
    },
    // Stripe card data (FunctionHelpers::stripeDataForAddingCard) for the embedded switch
    // and the combined checkout's inline card fields.
    stripeData: {
        type: Object,
        default: null,
    },
    // 86bc9e403: the feature checklist is hidden on /pricing (the page already lists them);
    // shown everywhere else.
    showFeatures: {
        type: Boolean,
        default: true,
    },
    // The card already on file, if any ({ card_brand, last_4 }). When present the combined
    // checkout shows it with a "Change" option instead of empty card fields.
    userPaymentMethod: {
        type: Object,
        default: null,
    },
    // The caller's SubscriptionProtection instance. Its presence switches the pop-up into the
    // in-place combined-checkout mode and is how the pop-up subscribes.
    subscription: {
        type: Object,
        default: null,
    },
})

const emit = defineEmits(['upgrade', 'subscribe', 'decline', 'dismiss', 'card-saved'])

const features = [
    'Digitally Sign Docs',
    'Anti-fraud Verification',
    'Expedited ACH Payments',
    'Access All Real Estate Docs',
    'Unlimited Application Views',
    'State-Specific Lease Agreements',
]

const page = usePage()
const showSwitch = ref(false)
const cardFields = ref(null)
const changeCard = ref(false)
const cardComplete = ref(false)
const submitting = ref(false)
const subscribeError = ref('')

// 86bc9e403 #B: a SetupIntent is spent once a card is saved against it. When the landlord
// starts changing the card we fetch a FRESH SetupIntent and remount CardFields (via the key),
// so the next confirmCardSetup runs against an unspent intent instead of the consumed one.
const freshStripeData = ref(null)
const cardFieldsKey = ref(0)
const effectiveStripeData = computed(() => freshStripeData.value ?? props.stripeData)

const resolvedProPlan = computed(() => props.proPlan ?? page.props.auth?.user?.pro_plan ?? null)
const proTiers = computed(() => page.props.subscription_prices?.pro_tiers ?? [])
const lowestTier = computed(() => proTiers.value[0] ?? null)
const startingAtRows = computed(() => buildStartingAtRows(proTiers.value))
const isLegacy = computed(() => resolvedProPlan.value?.state === 'legacy')
const quoteTier = computed(() => resolvedProPlan.value?.quote_tier ?? null)
const propertyCount = computed(() => resolvedProPlan.value?.property_count ?? 0)

// Combined-checkout mode is on when a caller wires its SubscriptionProtection ref. Legacy
// landlords always use the embedded "Switch to new plan", never the combined checkout.
const combinedMode = computed(() => !!props.subscription && !isLegacy.value)

// 86bc9e403: dashboard entry points (showFeatures) open on an "Upgrade to Pro" CTA and reveal
// the checkout only after it is clicked. /pricing (showFeatures false) shows the checkout at
// once. Legacy keeps its own "Switch to new plan" flow and is never gated behind the CTA.
const expanded = ref(false)
const showUpgradeCta = computed(() => props.showFeatures && combinedMode.value && !expanded.value)
const showCheckout = computed(() => !combinedMode.value || !props.showFeatures || expanded.value)
// A card the landlord saved inside THIS checkout — so a retry after a decline shows it and
// subscribes against it even on pages that don't refresh the user-payment-method prop.
const savedPaymentMethod = ref(null)
const effectivePaymentMethod = computed(() => savedPaymentMethod.value ?? props.userPaymentMethod)
const hasOnFileCard = computed(() => !!effectivePaymentMethod.value)

// The server floors the billed count at the landlord's real countable properties, so the
// input can never go below it. A brand-new landlord (0 properties) starts at 1.
const countFloor = computed(() => Math.max(1, propertyCount.value))

// In combined mode the count input is always shown, pre-filled with the countable count. In
// legacy mode it is only shown to a brand-new landlord with nothing to count (property_count
// === 0), who otherwise would have no tier to price.
const needsPropertyCount = computed(() => !isLegacy.value && propertyCount.value === 0)
const showPropertyCountInput = computed(() => combinedMode.value || needsPropertyCount.value)

const enteredPropertyCount = ref('')

watch([() => props.show, combinedMode, countFloor], ([isShown, , floor]) => {
    if (!isShown || !combinedMode.value) {
        return
    }

    // Prefill the count on open.
    if (enteredPropertyCount.value === '') {
        enteredPropertyCount.value = String(floor)

        return
    }

    // 86bc9e403 #A: a tier-boundary 409 ("price changed") reloads auth and can raise the floor
    // above the entered value (e.g. the landlord added an 11th property in another tab: 5 -> 11).
    // Bump the input up to the new floor instead of silently keeping an invalid lower number,
    // which would blank the price line and leave Subscribe disabled with no cue.
    const entered = Number.parseInt(enteredPropertyCount.value, 10)

    if (!Number.isInteger(entered) || entered < floor) {
        enteredPropertyCount.value = String(floor)
    }
}, { immediate: true })

const enteredCountNumber = computed(() => {
    const n = Number.parseInt(enteredPropertyCount.value, 10)

    return Number.isInteger(n) && n >= countFloor.value ? n : null
})

// 86bc9e403 #A: a count below the floor blanks the price and disables Subscribe; the hint tells
// the landlord why (the floor can also rise mid-checkout after a tier-boundary 409).
const belowFloor = computed(() => {
    if (!combinedMode.value || enteredPropertyCount.value === '') {
        return false
    }

    const entered = Number.parseInt(enteredPropertyCount.value, 10)

    return !Number.isInteger(entered) || entered < countFloor.value
})

const effectiveCount = computed(() => {
    if (combinedMode.value) {
        return enteredCountNumber.value
    }

    return needsPropertyCount.value ? enteredCountNumber.value : propertyCount.value
})

function tierForEnteredCount(count) {
    return proTiers.value.find((tier) => count >= tier.min && (tier.max == null || count <= tier.max)) ?? null
}

const effectiveTier = computed(() => {
    if (combinedMode.value) {
        return enteredCountNumber.value ? tierForEnteredCount(enteredCountNumber.value) : null
    }

    if (needsPropertyCount.value) {
        return enteredCountNumber.value ? tierForEnteredCount(enteredCountNumber.value) : null
    }

    return quoteTier.value
})

// 86bckgczc: describe the annual Pro charge to Apple Pay so it issues a merchant token, which
// keeps renewals working after the landlord changes devices. Amount is in cents.
const applePayRecurring = computed(() => {
    if (!effectiveTier.value) {
        return null
    }

    return {
        paymentDescription: 'RiskGuard Pro',
        managementURL: route('payment-methods.index'),
        regularBilling: {
            amount: Math.round(Number(effectiveTier.value.price) * 100),
            label: 'RiskGuard Pro (billed annually)',
            recurringPaymentIntervalUnit: 'year',
            recurringPaymentIntervalCount: 1,
        },
    }
})

// The card is ready when one is on file (and not being changed) or the inline fields are
// fully entered.
const cardReady = computed(() => (hasOnFileCard.value && !changeCard.value) || cardComplete.value)

const canSubmit = computed(() => {
    if (submitting.value || props.processing) {
        return false
    }

    if (isLegacy.value) {
        return true
    }

    if (!effectiveTier.value) {
        return false
    }

    if (combinedMode.value) {
        return cardReady.value
    }

    return !needsPropertyCount.value || enteredCountNumber.value !== null
})

const primaryLabel = computed(() => {
    if (submitting.value || props.processing) {
        return 'Processing...'
    }

    if (isLegacy.value) {
        return 'Switch to new plan'
    }

    return combinedMode.value ? 'Subscribe' : 'Yes, Upgrade to Pro'
})

// 86bc9e403 #B: fetch a fresh SetupIntent before the next card entry. stripeDataForAddingCard()
// returns a brand-new setup_intent, so remounting CardFields against it (cardFieldsKey) means a
// second card attempt never reuses the intent an earlier save already consumed.
async function refreshSetupIntent() {
    try {
        const { data } = await axios.get(route('protect-subscription.card-setup-data'))
        freshStripeData.value = data
    } catch {
        // Keep the current SetupIntent; confirmAndSave surfaces any save failure to the landlord.
    }

    cardFieldsKey.value += 1
}

function startChangeCard() {
    changeCard.value = true
    cardComplete.value = false
    refreshSetupIntent()
}

function storePendingPlan() {
    try {
        sessionStorage.setItem('pending_subscription_plan', JSON.stringify({
            planSlug: 'protect-pro-tiered',
            billingCycle: 'annual',
            expectedTierSlug: effectiveTier.value?.slug ?? null,
            propertyCount: effectiveCount.value,
        }))
        sessionStorage.setItem('subscription_success_message', 'Successfully subscribed to RiskGuard Pro!')
    } catch {
        // sessionStorage unavailable (private mode); the plan still rides along as props.
    }
}

// 86bc9e403 #4: the pop-up also hands the count + tier to SubscriptionProtection directly, so
// a sessionStorage write that threw (private mode) can't drop the entered count and silently
// price a new landlord at tier 1.
function planFallback() {
    return { propertyCount: effectiveCount.value, expectedTierSlug: effectiveTier.value?.slug ?? null }
}

async function runCombinedSubscribe() {
    if (!effectiveTier.value || !props.subscription) {
        return
    }

    subscribeError.value = ''
    submitting.value = true

    // An on-file card that is not being replaced subscribes straight away (Case 1).
    if (hasOnFileCard.value && !changeCard.value) {
        storePendingPlan()
        emit('subscribe', { propertyCount: effectiveCount.value, tierSlug: effectiveTier.value?.slug ?? null })
        // A card saved earlier in this checkout is on the server even if the parent's
        // user-payment-method prop hasn't refreshed — reload-then-subscribe picks it up. A
        // pre-existing on-file card can subscribe straight away.
        if (savedPaymentMethod.value) {
            props.subscription.subscribeAfterCardSaved(planFallback())
        } else {
            props.subscription.initiateSubscription(null, planFallback())
        }

        return
    }

    // A new or changed card is saved first. Only once it is safely on file do we flip the
    // parent's processing flag (emit 'subscribe') and hand off — so a card-save failure leaves
    // the button free to retry instead of stuck behind the parent's still-true processing flag.
    const result = await cardFields.value?.confirmAndSave({ replaceExisting: changeCard.value })

    if (!result?.success) {
        submitting.value = false
        subscribeError.value = result?.message || 'We could not save your card. Please try again.'

        return
    }

    // The card is now on file. Surface it so a reopened pop-up (e.g. after a later subscribe
    // failure) shows the saved card and "Change", not a now-consumed SetupIntent's CardFields.
    emit('card-saved', result.payment_method ?? null)
    savedPaymentMethod.value = result.payment_method ?? null

    // 86bc9e403: the SetupIntent this card consumed is now spent. Route any retry (e.g. after this
    // subscribe is declined) through the on-file-card path instead of re-confirming the dead intent:
    // clear changeCard/cardComplete so a second Subscribe click takes Case 1 with the just-saved
    // card, which the parent renders from the `card-saved` update above.
    changeCard.value = false
    cardComplete.value = false

    storePendingPlan()
    emit('subscribe', { propertyCount: effectiveCount.value, tierSlug: effectiveTier.value?.slug ?? null })
    props.subscription.subscribeAfterCardSaved(planFallback())
}

function primaryAction() {
    if (isLegacy.value) {
        showSwitch.value = true

        return
    }

    if (combinedMode.value) {
        runCombinedSubscribe()

        return
    }

    // Legacy emit-based path: the caller stores the plan and subscribes.
    if (needsPropertyCount.value) {
        if (enteredCountNumber.value === null) {
            return
        }

        emit('upgrade', { propertyCount: enteredCountNumber.value, tierSlug: effectiveTier.value?.slug ?? null })

        return
    }

    emit('upgrade')
}

// 86bc9e403 #1: the parent clears `processing` when a subscribe attempt resolves — a success,
// a declined card, or a 409 price change that reopens this same (never-closed) pop-up with a
// new price. Re-enable the button and drop any stale card-save error so the landlord can
// confirm the new price or retry; the parent surfaces the failure message via the `notice`
// prop. Card-save failures never set `processing`, so their error is left in place.
watch(() => props.processing, (isProcessing) => {
    if (!isProcessing) {
        submitting.value = false
        subscribeError.value = ''
    }
})

// Reset transient state each time the pop-up is dismissed and reopened so a prior attempt's
// error or "change card" toggle never leaks into the next one.
watch(() => props.show, (isShown) => {
    if (!isShown) {
        submitting.value = false
        subscribeError.value = ''
        changeCard.value = false
        cardComplete.value = false
        savedPaymentMethod.value = null
        // Collapse back to the CTA so the next dashboard open starts on step one again.
        expanded.value = false
        // Drop any fresh SetupIntent fetched for a "Change card" so the next open starts from
        // the parent's current stripeData rather than a possibly-consumed refetched intent.
        freshStripeData.value = null
        // Re-prefill to the current floor on the next open (the count may have moved the
        // floor up — e.g. after a tier-changed reopen).
        enteredPropertyCount.value = ''
    }
})

function closeSwitch() {
    showSwitch.value = false
    emit('dismiss')
}

defineExpose({ tierForEnteredCount })
</script>
